/**
 * Admin learner archive — every archived learner, with a Restore button.
 *
 * Mirrors AdminDashboardPage's auth/loading/forbidden plumbing. The list
 * is read from ``GET /api/admin/learners?archived=true``; the action is a
 * confirm-then-POST to /restore. Self-gated like the rest of the admin
 * pages: 401 → login redirect, 403 → notice.
 */
import type * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  type LearnerListResponse,
  type LearnerListRow,
  deleteLearner,
  listLearners,
  restoreLearner,
} from "@/lib/admin-api";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: LearnerListResponse }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

interface PendingRestore {
  userId: string;
  displayName: string;
}

interface PendingDelete {
  userId: string;
  displayName: string;
  /** What the admin must type to enable the confirm button. */
  typedConfirmTarget: string;
}

function redirectToLogin(returnTo: string): void {
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encodeURIComponent(returnTo)}`;
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

export function AdminLearnerArchivePage(): React.JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [pendingRestore, setPendingRestore] =
    useState<PendingRestore | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [deleteTyped, setDeleteTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoad({ kind: "loading" });
    try {
      const data = await listLearners({ archived: true });
      setLoad({ kind: "ready", data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToLogin("/admin/learners/archived");
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setLoad({ kind: "forbidden" });
        return;
      }
      const msg =
        err instanceof Error ? err.message : "Failed to load archived learners.";
      setLoad({ kind: "error", message: msg });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onRequestRestore = useCallback((row: LearnerListRow) => {
    setError(null);
    setPendingRestore({
      userId: row.user_id,
      displayName: row.name || row.email,
    });
  }, []);

  const onRequestDelete = useCallback((row: LearnerListRow) => {
    setError(null);
    setDeleteTyped("");
    setPendingDelete({
      userId: row.user_id,
      displayName: row.name || row.email,
      // Force the admin to type the learner's email — same string the
      // backend stores. Defensive against muscle-memory "yes proceed".
      typedConfirmTarget: row.email,
    });
  }, []);

  const onCancel = useCallback(() => {
    if (busy) return;
    setPendingRestore(null);
    setPendingDelete(null);
    setDeleteTyped("");
  }, [busy]);

  const onConfirmRestore = useCallback(async (): Promise<void> => {
    if (!pendingRestore) return;
    setBusy(true);
    setError(null);
    try {
      await restoreLearner(pendingRestore.userId);
      setPendingRestore(null);
      await refresh();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not restore learner.",
      );
    } finally {
      setBusy(false);
    }
  }, [pendingRestore, refresh]);

  const onConfirmDelete = useCallback(async (): Promise<void> => {
    if (!pendingDelete) return;
    if (deleteTyped.trim() !== pendingDelete.typedConfirmTarget) return;
    setBusy(true);
    setError(null);
    try {
      await deleteLearner(pendingDelete.userId);
      setPendingDelete(null);
      setDeleteTyped("");
      await refresh();
    } catch (err) {
      // ApiError messages are server-side; pass them straight through so
      // the 400 ("archive first") / 409 ("signed-in learner") hints are
      // visible to the admin.
      setError(
        err instanceof Error ? err.message : "Could not delete learner.",
      );
    } finally {
      setBusy(false);
    }
  }, [pendingDelete, deleteTyped, refresh]);

  if (load.kind === "forbidden") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">
          Admin access required
        </h1>
        <p className="mt-4 text-muted-foreground">
          The learner archive is only available to admin accounts. Sign in with
          an admin account to continue.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <header className="flex items-center justify-between border-b border-border pb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Archived learners
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Learners hidden from the active dashboard and at-risk digest.
            Restoring brings them back to the dashboard immediately — their
            enrollment, progress, and community profile are untouched.
          </p>
        </div>
        <Link
          to="/admin"
          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
        >
          ← Back to dashboard
        </Link>
      </header>

      <section className="mt-6 overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
        {load.kind === "loading" ? (
          <p className="px-6 py-12 text-center text-muted-foreground">
            Loading…
          </p>
        ) : load.kind === "error" ? (
          <p className="px-6 py-12 text-center text-rose-700">{load.message}</p>
        ) : load.data.learners.length === 0 ? (
          <p className="px-6 py-12 text-center text-muted-foreground">
            No archived learners.
          </p>
        ) : (
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-background">
              <tr>
                {["Learner", "Email", "Last active"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    {h}
                  </th>
                ))}
                <th
                  scope="col"
                  className="sticky right-0 z-10 bg-background px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground shadow-[inset_1px_0_0_theme(colors.border)]"
                >
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {load.data.learners.map((row) => (
                <tr key={row.user_id}>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/learners/${row.user_id}`}
                      className="font-medium text-foreground underline-offset-2 hover:underline"
                    >
                      {row.name || row.email}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {row.email}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDate(row.last_activity_at)}
                  </td>
                  <td className="sticky right-0 z-10 bg-white px-4 py-3 text-right shadow-[inset_1px_0_0_theme(colors.border)]">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onRequestRestore(row)}
                        className="rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-50"
                        aria-label={`Restore ${row.name || row.email}`}
                      >
                        Restore
                      </button>
                      <button
                        type="button"
                        onClick={() => onRequestDelete(row)}
                        className="rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-medium text-rose-800 hover:bg-rose-50"
                        aria-label={`Delete ${row.name || row.email} permanently`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {error ? (
        <p className="mt-3 text-sm text-rose-700" role="alert">
          {error}
        </p>
      ) : null}

      <ConfirmDialog
        open={pendingRestore !== null}
        title="Restore this learner?"
        description={
          pendingRestore ? (
            <span>
              <strong>{pendingRestore.displayName}</strong> will reappear on
              the active dashboard and become eligible again for the weekly
              at-risk digest.
            </span>
          ) : (
            ""
          )
        }
        confirmLabel="Restore learner"
        confirmTone="primary"
        busy={busy}
        onConfirm={onConfirmRestore}
        onCancel={onCancel}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Permanently delete this learner?"
        description={
          pendingDelete ? (
            <div>
              <p>
                This deletes <strong>{pendingDelete.displayName}</strong> and
                their enrollments from the database.{" "}
                <strong>This cannot be undone.</strong> Only works on
                never-signed-in test accounts &mdash; the API will refuse if
                they have any engagement history.
              </p>
              <label className="mt-4 block">
                <span className="block text-xs font-medium text-foreground">
                  Type{" "}
                  <code className="rounded bg-muted px-1 py-0.5 text-[0.75rem]">
                    {pendingDelete.typedConfirmTarget}
                  </code>{" "}
                  to enable the delete:
                </span>
                <input
                  type="text"
                  value={deleteTyped}
                  onChange={(e) => setDeleteTyped(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                  aria-label="Type the learner's email to confirm deletion"
                  autoFocus
                />
              </label>
            </div>
          ) : (
            ""
          )
        }
        confirmLabel="Delete permanently"
        confirmTone="danger"
        busy={busy}
        confirmDisabled={
          pendingDelete === null ||
          deleteTyped.trim() !== pendingDelete.typedConfirmTarget
        }
        onConfirm={onConfirmDelete}
        onCancel={onCancel}
      />
    </div>
  );
}
